/**
 * Project-level department assignments and contact persons.
 *
 * Schema layout:
 *   project_departments       — links a project to a department for a role
 *   project_department_contacts — one or more contact persons within that link
 *
 * Constraints:
 *   - At most one assignment per project role
 *   - Roles are project assignment headings, not application user roles
 *   - Assigned department may be either a client department or sub-department
 *   - At most one primary contact per project_department block
 */

import { Type, type Static } from '@sinclair/typebox';
import { DepartmentRoles } from '../enums.js';
import { FIELD_LIMITS } from '../constants.js';
import { RowVersionField, TimestampFields, UuidSchema } from './common.js';

// ---------------------------------------------------------------------------
// Department assignment
// ---------------------------------------------------------------------------

export const ProjectDepartmentSchema = Type.Object({
  id: UuidSchema,
  projectId: UuidSchema,
  departmentId: UuidSchema,
  departmentRole: Type.Union(DepartmentRoles.map((r) => Type.Literal(r))),
  ...TimestampFields,
});
export type ProjectDepartment = Static<typeof ProjectDepartmentSchema>;

// ---------------------------------------------------------------------------
// Contact person within a department block
// ---------------------------------------------------------------------------

export const ProjectDepartmentContactSchema = Type.Object({
  id: UuidSchema,
  projectDepartmentId: UuidSchema,
  name: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX }),
  designation: Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  officialPhone: Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  personalPhone: Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  email: Type.Union([Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX }), Type.Null()]),
  fax: Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  isPrimary: Type.Boolean(),
  ...TimestampFields,
  ...RowVersionField,
});
export type ProjectDepartmentContact = Static<typeof ProjectDepartmentContactSchema>;

// ---------------------------------------------------------------------------
// Composite read shape for the Contacts tab
// ---------------------------------------------------------------------------

export const ProjectDepartmentWithContactsSchema = Type.Composite([
  ProjectDepartmentSchema,
  Type.Object({
    departmentName: Type.String(), // joined from department master
    departmentParentId: Type.Union([UuidSchema, Type.Null()]),
    departmentParentName: Type.Union([Type.String(), Type.Null()]),
    contacts: Type.Array(ProjectDepartmentContactSchema),
  }),
]);
export type ProjectDepartmentWithContacts = Static<typeof ProjectDepartmentWithContactsSchema>;

// ---------------------------------------------------------------------------
// Create / update inputs
// ---------------------------------------------------------------------------

export const ProjectDepartmentCreateSchema = Type.Object({
  departmentId: UuidSchema,
  departmentRole: Type.Union(DepartmentRoles.map((r) => Type.Literal(r))),
});
export type ProjectDepartmentCreate = Static<typeof ProjectDepartmentCreateSchema>;

export const ProjectDepartmentContactCreateSchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX }),
  designation: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),
  officialPhone: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),
  personalPhone: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),
  email: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX })),
  fax: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),
  isPrimary: Type.Optional(Type.Boolean()),
});
export type ProjectDepartmentContactCreate = Static<typeof ProjectDepartmentContactCreateSchema>;

export const ProjectDepartmentContactUpdateSchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX })),
  designation: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),
  officialPhone: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),
  personalPhone: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),
  email: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX }), Type.Null()]),
  ),
  fax: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),
  isPrimary: Type.Optional(Type.Boolean()),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type ProjectDepartmentContactUpdate = Static<typeof ProjectDepartmentContactUpdateSchema>;
